import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ImageIcon, Pencil, Plus, RotateCcw, Tags, Trash2 } from 'lucide-react';
import { parseAsBoolean, parseAsString, parseAsStringLiteral, useQueryStates } from 'nuqs';
import { useState } from 'react';
import { toast } from 'sonner';
import type { Paginated } from '@barbeiropro/contracts';
import { ConfirmDialog } from '@/components/shared/confirm-dialog';
import { DataTable, type DataColumn } from '@/components/shared/data-table';
import { PageHeader } from '@/components/shared/page-header';
import { useTableParams } from '@/components/shared/use-table-params';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api, errorMessage } from '@/lib/api';
import { money } from '@/lib/format';
import { useAssetUrl, useCategories, type ServiceRow } from './api';
import { CategoriesModal } from './categories-modal';
import { ServiceFormModal } from './service-form';

const ALL = 'todos';

function Thumb({ id }: { id: string | null }) {
  const url = useAssetUrl(id);
  return (
    <div className="flex size-9 shrink-0 items-center justify-center overflow-hidden rounded-md border bg-muted">
      {url ? <img src={url} alt="" className="size-full object-cover" loading="lazy" /> : <ImageIcon className="size-4 text-muted-foreground" aria-hidden />}
    </div>
  );
}

export function ServicesPage() {
  const qc = useQueryClient();
  const params = useTableParams({ sort: 'name' });
  const [filters, setFilters] = useQueryStates(
    { categoria: parseAsString, ativo: parseAsStringLiteral(['true', 'false'] as const), excluidos: parseAsBoolean.withDefault(false) },
    { history: 'replace' },
  );
  const categories = useCategories();
  const [editing, setEditing] = useState<ServiceRow | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);

  const query = {
    ...params.query,
    categoryId: filters.categoria ?? undefined,
    active: filters.ativo ?? undefined,
    includeDeleted: filters.excluidos ? 'true' : undefined,
  };
  const list = useQuery({
    queryKey: ['catalog', 'services', 'list', query],
    queryFn: () => api.get<Paginated<ServiceRow>>('/services', query),
    placeholderData: (prev) => prev,
  });

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['catalog', 'services'] });
    qc.invalidateQueries({ queryKey: ['barbers'] });
  };
  const remove = async (s: ServiceRow) => {
    try {
      await api.del(`/services/${s.id}`);
      toast.success('Serviço excluído');
      refresh();
    } catch (e) {
      toast.error(errorMessage(e));
    }
  };
  const restore = useMutation({
    mutationFn: (s: ServiceRow) => api.post(`/services/${s.id}/restore`),
    onSuccess: () => {
      toast.success('Serviço restaurado');
      refresh();
    },
    onError: (e) => toast.error(errorMessage(e)),
  });

  const columns: DataColumn<ServiceRow>[] = [
    {
      id: 'name',
      header: 'Serviço',
      meta: { sortKey: 'name' },
      cell: ({ row }) => (
        <div className="flex min-w-0 items-center gap-3">
          <Thumb id={row.original.imageAssetId} />
          <div className="min-w-0">
            <p className="truncate font-medium">{row.original.name}</p>
            {row.original.description && <p className="max-w-xs truncate text-xs text-muted-foreground">{row.original.description}</p>}
          </div>
        </div>
      ),
    },
    { id: 'category', header: 'Categoria', cell: ({ row }) => row.original.categoryName ?? <span className="text-muted-foreground">Sem categoria</span> },
    {
      id: 'durationMinutes',
      header: 'Duração',
      meta: { sortKey: 'durationMinutes' },
      cell: ({ row }) => <span className="tabular">{row.original.durationMinutes} min</span>,
    },
    {
      id: 'priceCents',
      header: 'Preço',
      meta: { sortKey: 'priceCents', align: 'right' },
      cell: ({ row }) => <span className="tabular">{money(row.original.priceCents)}</span>,
    },
    { id: 'barbers', header: 'Profissionais', cell: ({ row }) => <span className="tabular">{row.original.barbersCount}</span> },
    {
      id: 'status',
      header: 'Situação',
      cell: ({ row }) =>
        row.original.deletedAt ? (
          <Badge variant="outline">Excluído</Badge>
        ) : (
          <div className="flex flex-wrap gap-1">
            <Badge variant={row.original.active ? 'secondary' : 'outline'}>{row.original.active ? 'Ativo' : 'Inativo'}</Badge>
            {!row.original.publicVisible && <Badge variant="outline">Oculto no site</Badge>}
          </div>
        ),
    },
    {
      id: 'actions',
      header: () => <span className="sr-only">Ações</span>,
      meta: { align: 'right' },
      cell: ({ row }) => {
        const s = row.original;
        return (
          <div className="flex justify-end gap-1">
            {s.deletedAt ? (
              <Button variant="outline" size="sm" onClick={() => restore.mutate(s)} disabled={restore.isPending}>
                <RotateCcw aria-hidden /> Restaurar
              </Button>
            ) : (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  aria-label={`Editar ${s.name}`}
                  onClick={() => {
                    setEditing(s);
                    setFormOpen(true);
                  }}
                >
                  <Pencil aria-hidden />
                </Button>
                <ConfirmDialog
                  trigger={
                    <Button variant="ghost" size="icon" aria-label={`Excluir ${s.name}`}>
                      <Trash2 className="text-destructive" aria-hidden />
                    </Button>
                  }
                  title={`Excluir "${s.name}"?`}
                  description='O serviço sai da agenda e do site, mas o histórico de atendimentos é mantido. Ele vai para "Excluídos" e pode ser restaurado quando quiser.'
                  confirmLabel="Excluir serviço"
                  onConfirm={() => remove(s)}
                />
              </>
            )}
          </div>
        );
      },
    },
  ];

  return (
    <div>
      <PageHeader
        title="Serviços"
        description="O que a barbearia oferece, com preço e duração padrão."
        actions={
          <>
            <Button variant="outline" onClick={() => setCatsOpen(true)}>
              <Tags aria-hidden /> Categorias
            </Button>
            <Button
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              <Plus aria-hidden /> Novo serviço
            </Button>
          </>
        }
      />
      <DataTable
        columns={columns}
        data={list.data}
        params={params}
        isLoading={list.isLoading}
        error={list.error}
        onRetry={() => list.refetch()}
        searchPlaceholder="Buscar serviço"
        filters={
          <>
            <Select value={filters.categoria ?? ALL} onValueChange={(v) => { setFilters({ categoria: v === ALL ? null : v }); params.set({ page: 1 }); }}>
              <SelectTrigger className="w-44" aria-label="Filtrar por categoria">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Todas as categorias</SelectItem>
                {categories.data?.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={filters.ativo ?? ALL} onValueChange={(v) => { setFilters({ ativo: v === ALL ? null : (v as 'true' | 'false') }); params.set({ page: 1 }); }}>
              <SelectTrigger className="w-36" aria-label="Filtrar por situação">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Qualquer situação</SelectItem>
                <SelectItem value="true">Ativos</SelectItem>
                <SelectItem value="false">Inativos</SelectItem>
              </SelectContent>
            </Select>
            <Button
              variant={filters.excluidos ? 'default' : 'outline'}
              size="sm"
              aria-pressed={filters.excluidos}
              onClick={() => { setFilters({ excluidos: !filters.excluidos }); params.set({ page: 1 }); }}
            >
              Excluídos
            </Button>
          </>
        }
        empty={{
          title: filters.excluidos ? 'Nenhum serviço excluído' : 'Nenhum serviço encontrado',
          description: filters.excluidos ? undefined : 'Cadastre os serviços para liberar a agenda e o agendamento online.',
          action: filters.excluidos ? undefined : (
            <Button
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              <Plus aria-hidden /> Novo serviço
            </Button>
          ),
        }}
      />
      <ServiceFormModal open={formOpen} onOpenChange={setFormOpen} service={editing} />
      <CategoriesModal open={catsOpen} onOpenChange={setCatsOpen} />
    </div>
  );
}
